// Procedural terrain heightmap — pure math, no rendering dependency so the
// render layer and (optionally) the sim can agree on ground elevation.
// Deterministic value-noise summed over a few octaves, seeded per map.

function hash2(ix: number, iz: number, seed: number): number {
  let h = (ix | 0) * 374761393 + (iz | 0) * 668265263 + (seed | 0) * 2246822519
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  h = h ^ (h >>> 16)
  return (h >>> 0) / 4294967295 // 0..1
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t)
}

function valueNoise(x: number, z: number, seed: number): number {
  const ix = Math.floor(x)
  const iz = Math.floor(z)
  const fx = x - ix
  const fz = z - iz
  const a = hash2(ix, iz, seed)
  const b = hash2(ix + 1, iz, seed)
  const c = hash2(ix, iz + 1, seed)
  const d = hash2(ix + 1, iz + 1, seed)
  const ux = smooth(fx)
  const uz = smooth(fz)
  return a * (1 - ux) * (1 - uz) + b * ux * (1 - uz) + c * (1 - ux) * uz + d * ux * uz
}

/** Stable numeric seed derived from a map id (FNV-1a). */
export function mapSeed(id: string): number {
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) % 100000
}

/** Spawn-flatten factor 0..1: a dead-flat safe pad near the origin, then a gentle ramp to full relief. */
function spawnFlatten(x: number, z: number): number {
  const d = Math.hypot(x, z)
  if (d <= 4) return 0 // flat starting pad — the run never begins on a slope, at any amp
  return smooth(Math.min(1, (d - 4) / 16)) // ramp to full relief by d≈20
}

/**
 * Per-biome terrain personality. The octave WEIGHTS are normalized out of the final
 * amplitude (see terrainHeight) so they change the CHARACTER (roughness / frequency mix)
 * not the overall height — height is set by the map's amp. More mid/ridge weight = rougher
 * (steeper, more cliffs); more continent weight + lower freq = big gentle traversable rolls.
 */
export interface TerrainConfig {
  continentFreq: number
  continentWeight: number
  ridgeFreq: number
  ridgeWeight: number
  midFreq: number
  midWeight: number
  detailWeight: number
}

export const DEFAULT_TERRAIN: TerrainConfig = {
  continentFreq: 0.012,
  continentWeight: 1.0,
  ridgeFreq: 0.04,
  ridgeWeight: 0.5,
  midFreq: 0.07,
  midWeight: 0.32,
  detailWeight: 0.18,
}

/** Merge a partial per-map terrain config onto the defaults. */
export function resolveTerrain(cfg?: Partial<TerrainConfig>): TerrainConfig {
  return cfg ? { ...DEFAULT_TERRAIN, ...cfg } : DEFAULT_TERRAIN
}

/**
 * Ground elevation at a world position. A layered macro field — a low-frequency
 * "continent" octave for broad basins/plateaus, a ridged mid layer that carves
 * walkable ridgelines, rolling mid hills, and fine detail — recentered on 0 and
 * normalized so the amplitude budget (~[-0.84,0.84]*amp) is independent of the
 * config weights. The spawn area is flattened so the start is always playable.
 * Pure function of (x,z,seed,amp,cfg): sim + render sample THIS so they agree.
 */
export function terrainHeight(x: number, z: number, seed: number, amp: number, cfg: TerrainConfig = DEFAULT_TERRAIN): number {
  if (amp <= 0) return 0
  const cw = cfg.continentWeight
  const rw = cfg.ridgeWeight
  const mw = cfg.midWeight
  const dw = cfg.detailWeight
  // continent: broad basins & plateaus (low freq = big, gentle, traversable rolls)
  let h = valueNoise(x * cfg.continentFreq + 100, z * cfg.continentFreq + 100, seed) * cw
  // ridges: ridged noise peaks along crests
  const r = valueNoise(x * cfg.ridgeFreq + 50, z * cfg.ridgeFreq + 50, seed + 17)
  h += (1 - Math.abs(2 * r - 1)) * rw
  // rolling mid-scale hills — visible undulation so the world reads as 3D terrain
  h += valueNoise(x * cfg.midFreq + 200, z * cfg.midFreq + 200, seed + 53) * mw
  // fine detail
  h += valueNoise(x * 0.12, z * 0.12, seed + 31) * dw
  // recenter + normalize by the half-sum so weight magnitudes set CHARACTER, not amplitude
  const half = (cw + rw + mw + dw) * 0.5
  h = ((h - half) / half) * 0.84
  return h * amp * spawnFlatten(x, z)
}

/**
 * Analytic-ish slope at a position via central differences on terrainHeight.
 * Returns the ground gradient {gx, gz} (rise per world unit). A FRESH object each
 * call — never a shared mutable scratch — so it is safe to call re-entrantly in a
 * hot loop (player + N enemies) without aliasing, preserving sim determinism.
 */
export function terrainGradient(x: number, z: number, seed: number, amp: number, cfg: TerrainConfig = DEFAULT_TERRAIN, eps = 0.5): { gx: number; gz: number } {
  if (amp <= 0) return { gx: 0, gz: 0 }
  const hx1 = terrainHeight(x + eps, z, seed, amp, cfg)
  const hx0 = terrainHeight(x - eps, z, seed, amp, cfg)
  const hz1 = terrainHeight(x, z + eps, seed, amp, cfg)
  const hz0 = terrainHeight(x, z - eps, seed, amp, cfg)
  const inv = 1 / (2 * eps)
  return { gx: (hx1 - hx0) * inv, gz: (hz1 - hz0) * inv }
}

/** Scalar steepness (rise/run magnitude of the gradient) at a position. */
export function terrainSlope(x: number, z: number, seed: number, amp: number, cfg: TerrainConfig = DEFAULT_TERRAIN, eps = 0.5): number {
  const g = terrainGradient(x, z, seed, amp, cfg, eps)
  return Math.hypot(g.gx, g.gz)
}

/** Unit surface normal at a position (y-up), derived from the gradient. */
export function terrainNormal(x: number, z: number, seed: number, amp: number, cfg: TerrainConfig = DEFAULT_TERRAIN, eps = 0.5): { x: number; y: number; z: number } {
  const g = terrainGradient(x, z, seed, amp, cfg, eps)
  const len = Math.hypot(g.gx, 1, g.gz) || 1
  return { x: -g.gx / len, y: 1 / len, z: -g.gz / len }
}

/**
 * Relief shade factor for top-down / elevation views (e.g. the minimap): high flat ground
 * reads bright, basins and steep faces read dark. `hn` is height/amp clamped to [-1,1];
 * `flat` is 1 (level) .. 0 (cliff). Clamped so colours stay in a sane visible band. Pure.
 */
export function reliefShade(hn: number, flat: number): number {
  const h = Math.max(-1, Math.min(1, hn))
  const f = Math.max(0, Math.min(1, flat))
  const s = (0.62 + h * 0.5) * (0.72 + 0.28 * f)
  return Math.max(0.24, Math.min(1.35, s))
}
